import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createMemoryHistory } from 'history';
import {
  BrowserRouter as BaseBrowserRouter,
  Link,
  LinkProps,
  Navigate,
  NavLink as BaseNavLink,
  Router as BaseRouter,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  matchPath as baseMatchPath,
  useBeforeUnload,
  useLocation,
  useNavigationType,
  useParams as useBaseParams,
} from 'react-router-dom';

export * from 'react-router-dom';

export type match<
  Params extends Record<string, string | undefined> = Record<string, string | undefined>,
> = {
  params: Params;
  path: string;
  url: string;
  isExact: boolean;
};

export type LegacyRouteRenderProps = {
  history: ReturnType<typeof useHistory>;
  location: ReturnType<typeof useLocation>;
  match: match;
};

export type RouteProps = {
  path?: string | string[];
  exact?: boolean;
  strict?: boolean;
  sensitive?: boolean;
  component?: React.ComponentType<any>;
  render?: (props: LegacyRouteRenderProps) => React.ReactNode;
  children?: React.ReactNode | ((props: LegacyRouteRenderProps) => React.ReactNode);
};

type PromptMessage =
  | string
  | boolean
  | ((location: ReturnType<typeof useLocation>, action: string) => string | boolean);

type PromptProps = {
  when?: boolean;
  message: PromptMessage;
};

type LastLocation = ReturnType<typeof useLocation> | null;

const LastLocationContext = React.createContext<LastLocation>(null);

const resolvePromptMessage = (
  message: PromptMessage,
  location: ReturnType<typeof useLocation>,
  action: string,
) => {
  if (typeof message === 'function') {
    return message(location, action);
  }

  return message;
};

const getWindowLocation = () => ({
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
  state: null,
  key: 'default',
});

const LegacyRouteParamsContext = React.createContext<Record<string, string | undefined>>({});

export function BrowserRouter({ forceRefresh, ...rest }: any) {
  void forceRefresh;
  return <BaseBrowserRouter {...rest} />;
}

export { Link };
export type { LinkProps };

type LegacyPathPattern =
  | string
  | {
      path?: string | string[];
      exact?: boolean;
      strict?: boolean;
      sensitive?: boolean;
    };

function normalizeMatchPathArgs(pathnameOrPattern: any, optionsOrPathname?: any) {
  // v5 shape: matchPath(pathname, { path, exact })
  if (
    typeof pathnameOrPattern === 'string' &&
    optionsOrPathname &&
    typeof optionsOrPathname === 'object'
  ) {
    return {
      pathname: pathnameOrPattern,
      path: optionsOrPathname.path,
      exact: Boolean(optionsOrPathname.exact),
      caseSensitive: Boolean(optionsOrPathname.sensitive),
    };
  }

  // v6 passthrough: matchPath(pattern, pathname)
  if (optionsOrPathname && typeof optionsOrPathname === 'string') {
    if (typeof pathnameOrPattern === 'string') {
      return {
        pathname: optionsOrPathname,
        path: pathnameOrPattern,
        exact: false,
        caseSensitive: false,
      };
    }

    return {
      pathname: optionsOrPathname,
      path: pathnameOrPattern.path,
      exact: pathnameOrPattern.end ?? false,
      caseSensitive: pathnameOrPattern.caseSensitive ?? false,
    };
  }

  // v5 alternate: matchPath(pathname, path)
  if (typeof pathnameOrPattern === 'string' && typeof optionsOrPathname === 'string') {
    return {
      pathname: pathnameOrPattern,
      path: optionsOrPathname,
      exact: false,
      caseSensitive: false,
    };
  }

  return {
    pathname: window.location.pathname,
    path: pathnameOrPattern,
    exact: false,
    caseSensitive: false,
  };
}

export function matchPath(pathnameOrPattern: any, optionsOrPathname?: any) {
  const normalized = normalizeMatchPathArgs(pathnameOrPattern, optionsOrPathname);

  if (!normalized.path) {
    return null;
  }

  const patterns = Array.isArray(normalized.path) ? normalized.path : [normalized.path];

  for (const currentPath of patterns) {
    const matched = baseMatchPath(
      {
        path: currentPath,
        end: normalized.exact,
        caseSensitive: normalized.caseSensitive,
      },
      normalized.pathname,
    );

    if (matched) {
      return {
        params: matched.params || {},
        path: currentPath,
        url: matched.pathname,
        isExact: matched.pathname === normalized.pathname,
      };
    }
  }

  return null;
}

export function Router({ history, children }: { history: any; children: React.ReactNode }) {
  const routerHistory = useMemo(() => history || createMemoryHistory(), [history]);
  const [state, setState] = useState({
    action: routerHistory.action,
    location: routerHistory.location,
  });

  useLayoutEffect(() => routerHistory.listen(setState), [routerHistory]);

  return (
    <BaseRouter location={state.location} navigationType={state.action} navigator={routerHistory}>
      {children}
    </BaseRouter>
  );
}

export function LastLocationProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const previousRef = useRef<LastLocation>(null);

  useEffect(() => {
    previousRef.current = location;
  }, [location]);

  const value = previousRef.current;

  return <LastLocationContext.Provider value={value}>{children}</LastLocationContext.Provider>;
}

export function useLastLocation() {
  return useContext(LastLocationContext);
}

export function useHistory() {
  const navigationContext = useContext(UNSAFE_NavigationContext as any);
  const locationContext = useContext(UNSAFE_LocationContext as any);
  const location = locationContext ? locationContext.location : getWindowLocation();
  const listenersRef = useRef(new Set<(nextLocation: any, action?: string) => void>());
  const navigationType = useNavigationType();

  useEffect(() => {
    listenersRef.current.forEach((listener) => listener(location, navigationType));
  }, [location, navigationType]);

  return useMemo(
    () => ({
      push: (to: any, state?: any) =>
        navigationContext && navigationContext.navigator.push(to, state),
      replace: (to: any, state?: any) =>
        navigationContext && navigationContext.navigator.replace(to, state),
      go: (delta: number) =>
        navigationContext ? navigationContext.navigator.go(delta) : window.history.go(delta),
      goBack: () =>
        navigationContext ? navigationContext.navigator.go(-1) : window.history.back(),
      goForward: () =>
        navigationContext ? navigationContext.navigator.go(1) : window.history.forward(),
      listen: (listener: (nextLocation: any, action?: string) => void) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
      location,
    }),
    [location, navigationContext],
  );
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  const params = useBaseParams<T>();
  const legacyParams = useContext(LegacyRouteParamsContext) as T;

  if (params && Object.keys(params).length > 0) {
    return params;
  }

  return legacyParams;
}

export function useRouteMatch(pattern?: LegacyPathPattern | LegacyPathPattern[]) {
  const location = useLocation();

  if (!pattern) {
    return {
      params: {},
      path: location.pathname,
      url: location.pathname,
      isExact: true,
    };
  }

  const patterns = Array.isArray(pattern) ? pattern : [pattern];

  for (const item of patterns) {
    const path = typeof item === 'string' ? item : item.path;
    const exact = typeof item === 'string' ? false : Boolean(item.exact);
    const sensitive = typeof item === 'string' ? false : Boolean(item.sensitive);

    const result = matchPath(location.pathname, { path, exact, sensitive });
    if (result) {
      return result;
    }
  }

  return null;
}

export function NavLink({ activeClassName, className, exact, ...props }: any) {
  const end = typeof exact === 'boolean' ? exact : undefined;

  return (
    <BaseNavLink
      {...props}
      end={end}
      className={({ isActive }) => {
        const resolvedClassName =
          typeof className === 'function' ? className({ isActive }) : className;

        if (!isActive || !activeClassName) {
          return resolvedClassName;
        }

        return resolvedClassName ? `${resolvedClassName} ${activeClassName}` : activeClassName;
      }}
    />
  );
}

function matchRoutePath(path: any, exact: boolean, pathname: string) {
  if (path == null) {
    return {
      params: {},
      path: pathname,
      url: pathname,
      isExact: true,
    };
  }

  return matchPath(pathname, { path, exact });
}

export function LegacyRoute({
  children,
  component: Component,
  exact = false,
  path,
  render,
}: RouteProps) {
  const locationContext = useContext(UNSAFE_LocationContext);
  const location = locationContext ? locationContext.location : getWindowLocation();
  const history = useHistory();
  const matched = matchRoutePath(path, exact, location.pathname);

  if (!matched) {
    return null;
  }

  const routeProps: LegacyRouteRenderProps = {
    history,
    location,
    match: {
      params: matched.params || {},
      path: (Array.isArray(path) ? path[0] : path) || '',
      url: matched.url,
      isExact: matched.isExact,
    },
  };

  const params = matched.params || {};

  const renderContent = () => {
    if (Component) {
      return <Component {...routeProps} />;
    }

    if (typeof render === 'function') {
      return render(routeProps);
    }

    if (typeof children === 'function') {
      return children(routeProps);
    }

    return <React.Fragment>{children}</React.Fragment>;
  };

  return (
    <LegacyRouteParamsContext.Provider value={params}>
      {renderContent()}
    </LegacyRouteParamsContext.Provider>
  );
}

export const Route = LegacyRoute;

export function Redirect({ to, push }: { to: any; push?: boolean }) {
  return <Navigate to={to} replace={!push} />;
}

export function Switch({ children, location }: { children: React.ReactNode; location?: any }) {
  const contextLocation = useLocation();
  const currentLocation = location || contextLocation;

  const childArray = React.Children.toArray(children);

  for (const child of childArray) {
    if (!React.isValidElement(child)) {
      continue;
    }

    const { path, from, exact } = child.props || {};
    const candidatePath = path ?? from;

    if (candidatePath == null) {
      return <React.Fragment>{child}</React.Fragment>;
    }

    const matched = matchPath(currentLocation.pathname, {
      path: candidatePath,
      exact: Boolean(exact),
    });

    if (matched) {
      return <React.Fragment>{child}</React.Fragment>;
    }
  }

  return null;
}

export function withRouter(Component: any) {
  function ComponentWithRouterProp(props: any) {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();

    const routedMatch = useMemo(
      () => ({
        params,
        path: location.pathname,
        url: location.pathname,
        isExact: true,
      }),
      [location.pathname, params],
    );

    return <Component {...props} history={history} location={location} match={routedMatch} />;
  }

  ComponentWithRouterProp.displayName = `withRouter(${
    Component.displayName || Component.name || 'Component'
  })`;

  return ComponentWithRouterProp;
}

export function Prompt({ when = true, message }: PromptProps) {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (!when || !navigationContext) {
      return undefined;
    }

    const navigator = navigationContext.navigator as {
      block?: (
        blocker: (transition: {
          action?: string;
          location: ReturnType<typeof useLocation>;
          retry: () => void;
        }) => void,
      ) => () => void;
    };

    if (typeof navigator.block !== 'function') {
      return undefined;
    }

    return navigator.block((transition) => {
      const result = resolvePromptMessage(
        message,
        transition.location,
        transition.action || navigationType,
      );

      if (result === true || result == null) {
        transition.retry();
        return;
      }

      if (result === false) {
        return;
      }

      if (window.confirm(result)) {
        transition.retry();
      }
    });
  }, [message, navigationContext, navigationType, when]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!when) {
          return;
        }

        const result = resolvePromptMessage(message, location, navigationType);

        if (result === true || result == null) {
          return;
        }

        event.preventDefault();
        event.returnValue = result === false ? '' : result;
      },
      [location, message, navigationType, when],
    ),
  );

  return null;
}
